import { useCallback, useEffect, useRef, useState } from 'react';
import { leadGenApi, type CallbackPreset } from '../services/leadGenApi';
import { presetToIso, isoToLocalInput, localInputToIso, labelFor, presetFromDate } from '../lib/callbackPresets';

/**
 * „Wann wieder anrufen?" — EINE Zeitwahl für beide Oberflächen.
 *
 * Sie stand ursprünglich nur im Anruf-Cockpit (Sales CRM). Auf der fraud0-Lead-Karte gab es
 * stattdessen ein nacktes `datetime-local`-Feld, und das auch nur bei vier von zehn Ergebnissen —
 * ausgerechnet nicht bei „Nicht erreicht", dem häufigsten. Zwei Oberflächen, die dasselbe Feld
 * (`domain_leads.callback_at`) schreiben, sollen sich nicht unterschiedlich anfühlen.
 *
 * Die Vorschläge kommen aus den Einstellungen (`lead_distribution.callback_presets`), gerechnet
 * wird in `lib/callbackPresets.ts`. Modul-Cache: die Liste ändert sich selten, und weder ein neuer
 * Anruf noch eine neue Karte soll etwas kosten.
 */
let presetCache: CallbackPreset[] | null = null;

export function useCallbackPresets() {
  const [presets, setPresets] = useState<CallbackPreset[]>(presetCache ?? []);
  const [presetIdx, setPresetIdx] = useState<number | null>(null);
  // UTC-ISO mit 'Z'. Der Server schreibt das nach domain_leads.callback_at (daraus sortiert der
  // Telefon-Stapel) und nach leads.follow_up_date.
  const [callbackAt, setCallbackAt] = useState<string | null>(null);
  const [exactOpen, setExactOpen] = useState(false);
  // Die Vorwahl kann gewünscht sein, BEVOR die Liste da ist (Erstaufruf holt sie asynchron).
  // Dann greift sie beim Eintreffen — sonst bliebe der erste Anruf nach dem Laden ohne Vorschlag.
  const vorwahlGewuenscht = useRef(false);

  const waehleStandard = useCallback((list: CallbackPreset[]) => {
    const i = list.findIndex((p) => p.default);
    if (i < 0) return;
    setPresetIdx(i);
    setCallbackAt(presetToIso(list[i]));
  }, []);

  // Schlägt das Holen fehl, bleibt callbackAt null — dann stempelt die serverseitige Auto-Kadenz
  // (call_cadence_days). Die Zeitwahl fällt also aufs bisherige Verhalten zurück, sie blockiert nie.
  useEffect(() => {
    if (presetCache) {
      setPresets(presetCache);
      if (vorwahlGewuenscht.current) waehleStandard(presetCache);
      return;
    }
    let abgebrochen = false;
    leadGenApi.getCallbackPresets()
      .then((r) => {
        if (abgebrochen) return;
        presetCache = r.presets || [];
        setPresets(presetCache);
        if (vorwahlGewuenscht.current) waehleStandard(presetCache);
      })
      .catch(() => { /* ohne Vorschläge übernimmt die Auto-Kadenz */ });
    return () => { abgebrochen = true; };
  }, [waehleStandard]);

  /** Standard-Vorschlag vorwählen (`true`) oder die Wahl fallen lassen (`false`). */
  const setzeVorwahl = useCallback((an: boolean) => {
    vorwahlGewuenscht.current = an;
    if (!an) {
      setPresetIdx(null);
      setCallbackAt(null);
      setExactOpen(false);
      return;
    }
    if (presetCache?.length) waehleStandard(presetCache);
  }, [waehleStandard]);

  /** Nur beim echten Kartenwechsel — damit Karte 2 nicht die Wahl von Karte 1 trägt. */
  const reset = useCallback(() => {
    vorwahlGewuenscht.current = false;
    standRef.current = null;
    setPresetIdx(null);
    setCallbackAt(null);
    setExactOpen(false);
  }, []);

  /**
   * Einen bereits gespeicherten Zeitpunkt anzeigen (gewinnt immer gegen die Vorwahl).
   *
   * Meint er denselben Augenblick wie die aktuelle Wahl, bleibt die Wahl stehen: nach dem Speichern
   * spielt die Karte ihr eigenes `callback_at` zurück, und der eben angetippte Chip hätte sonst
   * seine Markierung verloren — als wäre die Wahl weg, obwohl genau sie gespeichert wurde.
   */
  const standRef = useRef<string | null>(null);
  useEffect(() => { standRef.current = callbackAt; }, [callbackAt]);
  const setEigeneZeit = useCallback((iso: string | null) => {
    vorwahlGewuenscht.current = false;
    const a = standRef.current ? Date.parse(standRef.current) : NaN;
    const b = iso ? Date.parse(iso) : NaN;
    if (Number.isFinite(a) && a === b) return;
    setPresetIdx(null);
    setCallbackAt(iso);
  }, []);

  // Nochmal antippen wählt ab: dann greift wieder die Auto-Kadenz aus den Einstellungen.
  const waehlePreset = useCallback((i: number) => {
    const ab = presetIdx === i;
    vorwahlGewuenscht.current = false;
    setPresetIdx(ab ? null : i);
    setCallbackAt(ab || !presets[i] ? null : presetToIso(presets[i]));
    setExactOpen(false);
  }, [presetIdx, presets]);

  const waehleGenau = useCallback((iso: string | null) => {
    vorwahlGewuenscht.current = false;
    setPresetIdx(null);
    setCallbackAt(iso);
  }, []);

  const toggleExact = useCallback(() => setExactOpen((v) => !v), []);

  // „+ speichern": den frei gewählten Abstand als Vorschlag anhängen. Die Liste ist geteilt, der
  // neue Eintrag steht danach allen zur Wahl. Scheitert das, bleibt die Zeitwahl davon unberührt.
  const merkeAlsVorschlag = useCallback(async () => {
    if (!callbackAt) return;
    try {
      const r = await leadGenApi.addCallbackPreset(presetFromDate(new Date(callbackAt)));
      presetCache = r.presets || [];
      setPresets(presetCache);
    } catch { /* der gewählte Zeitpunkt gilt trotzdem */ }
  }, [callbackAt]);

  /**
   * Der Zeitpunkt, der JETZT gespeichert werden soll.
   *
   * Ein Vorschlag wird beim Speichern neu gerechnet, nicht beim Anklicken. Im Cockpit ist das
   * einerlei (ein Dialog lebt Minuten), auf der Lead-Karte nicht: die Werkbank steht Stunden im
   * Stapel, ein um 09:00 vorgewähltes „in 2h" hiesse um 14:00 sonst immer noch 11:00 — also
   * Vergangenheit, und der Lead käme sofort wieder.
   */
  const aktuellerIso = useCallback((): string | null => {
    if (presetIdx != null && presets[presetIdx]) return presetToIso(presets[presetIdx]);
    return callbackAt;
  }, [presetIdx, presets, callbackAt]);

  return {
    presets, presetIdx, callbackAt, exactOpen,
    setzeVorwahl, reset, setEigeneZeit,
    waehlePreset, waehleGenau, toggleExact, merkeAlsVorschlag,
    aktuellerIso,
  };
}

/** Vorschlags-Chips plus aufklappbare Feinwahl.
 *
 *  Ein Vorschlag kann beim Öffnen vorgewählt sein, damit „Niemand rangegangen" ein einziger
 *  Tastendruck bleibt; nochmal antippen wählt ab, dann greift die Auto-Kadenz aus den
 *  Einstellungen. Ziffern-Kürzel gibt es nur dort, wo sie frei sind (confirm/shortcall) — im
 *  Abschluss-Panel gehören 1-4 den Emotionen, im Wisch-Stapel dürfen Ziffern gar nichts setzen. */
export function CallbackPicker({
  presets, presetIdx, callbackAt, exactOpen, hotkeys, kannSpeichern, className = 'mb-4',
  onPreset, onExact, onToggleExact, onSavePreset,
}: {
  presets: CallbackPreset[];
  presetIdx: number | null;
  callbackAt: string | null;
  exactOpen: boolean;
  hotkeys: boolean;
  kannSpeichern: boolean;
  className?: string;
  onPreset: (i: number) => void;
  onExact: (iso: string | null) => void;
  onToggleExact: () => void;
  onSavePreset: () => void;
}) {
  const eigeneZeit = callbackAt != null && presetIdx == null;
  return (
    <div className={`rounded-xl border border-border-primary bg-bg-elevated/40 p-3 ${className}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-text-muted">Wieder anrufen</div>
        <div className="text-xs text-text-muted tabular-nums">
          {labelFor(callbackAt) ?? 'nach Rhythmus aus den Einstellungen'}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((p, i) => (
          <button
            key={`${p.label}-${i}`}
            onClick={() => onPreset(i)}
            className={`px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
              presetIdx === i
                ? 'bg-accent/15 border-accent text-accent'
                : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
            }`}
          >
            {p.label}
            {hotkeys && i < 9 && <span className="opacity-50 text-xs ml-1">{i + 1}</span>}
          </button>
        ))}
        <button
          onClick={onToggleExact}
          className={`px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
            eigeneZeit
              ? 'bg-accent/15 border-accent text-accent'
              : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
          }`}
        >
          {exactOpen ? '▾' : '▸'} genauer Zeitpunkt
        </button>
      </div>
      {exactOpen && (
        <div className="flex items-center gap-2 mt-2">
          <input
            type="datetime-local"
            value={callbackAt ? isoToLocalInput(callbackAt) : ''}
            onChange={(e) => onExact(localInputToIso(e.target.value))}
            className="flex-1 bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
          />
          {kannSpeichern && eigeneZeit && (
            <button
              onClick={onSavePreset}
              title="Diesen Abstand als Vorschlag merken — für alle sichtbar"
              className="px-3 py-2 text-sm rounded-lg border border-border-primary text-text-muted hover:text-text-main whitespace-nowrap"
            >
              + speichern
            </button>
          )}
        </div>
      )}
    </div>
  );
}
